import { useUnmount } from "@mediamonks/react-kit";
import { useAnimation, animations } from "@mediamonks/react-kit/gsap";
import { type RefObject } from "react";
import { useEnableAnimationsContext } from "../providers/EnableAnimationsProvider.context.ts";

/**
 * A hook that enables or disables animations based on the user's preference.
 * This hook wraps the useAnimation hook from @mediamonks/react-kit/gsap and
 * respects the animation settings from the EnableAnimationsContext.
 *
 * @example
 * // Basic usage
 * useEnabledAnimation(() => createInAnimation(refs), [refs]);
 *
 * @example
 * // With expose and reference parameters
 * const elementRef = useRef(null);
 * useEnabledAnimation(() => createInAnimation(refs), [refs], true, elementRef);
 */
export function useEnabledAnimation<T extends gsap.core.Animation>(
  callback: () => T | undefined,
  dependencies: ReadonlyArray<unknown>,
  expose?: boolean,
  reference?: RefObject<unknown>,
): ReturnType<typeof useAnimation> {
  const areAnimationsEnabled = useEnableAnimationsContext();

  const animationRef = useAnimation(() => {
    if (reference?.current && expose) {
      animations.delete(reference.current);
    }

    const animation = callback();

    if (!areAnimationsEnabled) {
      animation?.pause(0);
    }

    if (reference?.current && animation && expose) {
      animations.set(reference.current, animation);
    }

    return animation;
  }, [...dependencies, expose ? false : areAnimationsEnabled]);

  useUnmount(() => {
    if (reference?.current && expose) {
      animations.delete(reference.current);
    }
  });

  return animationRef;
}
